/**
 * Settings → "View declined synonym matches" (features/synonym_suggestions.md).
 *
 * Declines are per USER. Removing one only deletes the decline record, so the
 * pair can be suggested again the next time it occurs — it is NOT turned into
 * a synonym. Each Remove is its own server-side op (never part of the Settings
 * Save), so Cancel on the Settings dialog does not undo it.
 */
import { useEffect, useState } from 'react';
import { Loader2, X, Trash2, AlertCircle } from 'lucide-react';
import { api } from '../../services/api';
import type { SynonymDecline } from '../../types';
import { removeDecline } from '../../utils/synonymSuggestions';

function formatDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
}

function DeclinedSynonymsDialog({ onClose }: { onClose: () => void }) {
  const [declines, setDeclines] = useState<SynonymDecline[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.getPreferences()
      .then(p => { if (!cancelled) setDeclines(p.synonym_declines || []); })
      .catch(err => {
        if (cancelled) return;
        console.error('Failed to load declined synonym matches:', err);
        setLoadError('Could not load your declined synonym matches.');
      });
    return () => { cancelled = true; };
  }, []);

  // Escape closes THIS dialog only — capture on window so the Settings
  // dialog's own document-level Escape handler never sees the keystroke.
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
    };
    window.addEventListener('keydown', h, true);
    return () => window.removeEventListener('keydown', h, true);
  }, [onClose]);

  async function handleRemove(key: string) {
    setBusyKey(key);
    setActionError(null);
    const res = await removeDecline(key, k => api.removeSynonymDecline(k));
    setBusyKey(null);
    if (res.ok) setDeclines(res.declines);
    else {
      console.error('Failed to remove declined synonym match:', res.error);
      setActionError('Could not remove that match — please try again.');
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[60] p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Declined synonym matches"
      data-testid="declined-synonyms-dialog"
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-base font-semibold text-gray-900">Declined synonym matches</h3>
          <button type="button" onClick={onClose} className="p-1 hover:bg-gray-100 rounded" aria-label="Close">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <p className="text-xs text-gray-500 mb-3">
            Pairs you told SkillSnap are not interchangeable. They are never suggested again.
            Removing one lets it be suggested again the next time it comes up — it does not add it as a synonym.
          </p>
          {loadError ? (
            <div role="alert" className="flex items-center p-2 bg-red-50 text-red-700 rounded-lg text-sm">
              <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" /> {loadError}
            </div>
          ) : declines === null ? (
            <div className="flex justify-center py-6" role="status" aria-label="Loading declined synonym matches">
              <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
            </div>
          ) : declines.length === 0 ? (
            <p className="text-sm text-gray-500 py-4 text-center" data-testid="declined-synonyms-empty">
              You haven&apos;t declined any synonym matches.
            </p>
          ) : (
            <ul className="divide-y divide-gray-100" data-testid="declined-synonyms-list">
              {declines.map(d => (
                <li key={d.pair_key} className="flex items-center gap-3 py-2" data-testid="declined-synonym" data-pair-key={d.pair_key}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-800 break-words">{d.terms.join(' // ')}</p>
                    {formatDate(d.declined_at) && (
                      <p className="text-xs text-gray-400">Declined {formatDate(d.declined_at)}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemove(d.pair_key)}
                    disabled={busyKey === d.pair_key}
                    data-testid="declined-synonym-remove"
                    className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50"
                  >
                    {busyKey === d.pair_key ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
          {actionError && <p className="text-xs text-red-600 mt-2" role="alert">{actionError}</p>}
        </div>
      </div>
    </div>
  );
}

export function SynonymDeclinesSection() {
  const [open, setOpen] = useState(false);
  return (
    <div className="px-3 pb-3">
      <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
        <div className="flex flex-col">
          <span className="text-sm text-gray-700">Synonym suggestions</span>
          <span className="text-xs text-gray-500">Word pairs you declined in Define Synonyms or on a job</span>
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-help-target="mainapp.user.settings.resumegen.synonymdeclines"
          data-testid="view-declined-synonyms"
          className="px-3 py-1.5 text-xs text-primary-700 border border-primary-200 bg-white rounded-lg hover:bg-primary-50 whitespace-nowrap"
        >
          View declined synonym matches
        </button>
      </div>
      {open && <DeclinedSynonymsDialog onClose={() => setOpen(false)} />}
    </div>
  );
}
